import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { ArrowRight, Heart, Image as ImageIcon, Search } from "lucide-react";
import { toast } from "sonner";
import { listMyOffers, listSavedOpportunityIds, toggleSavedOpportunity } from "@/lib/partner.functions";
import { gbp } from "@/lib/format";
import { AppHeader } from "@/components/site/AppHeader";
import { EmptyState, PageHeader, PageShell } from "@/components/ui/shields-layout";

const offersQuery=queryOptions({queryKey:["partner-offers"],queryFn:()=>listMyOffers()});
const savedOffersQuery=queryOptions({queryKey:["partner-saved-opportunities"],queryFn:()=>listSavedOpportunityIds()});
export const Route=createFileRoute("/_authenticated/partner/offers/")({head:()=>({meta:[{title:"Opportunities — Shields Partner"},{name:"robots",content:"noindex"}]}),component:PartnerOpportunities});

type Tab="live"|"invites"|"saved";

function PartnerOpportunities(){
 const {data:offers}=useSuspenseQuery(offersQuery);
 const {data:saved}=useSuspenseQuery(savedOffersQuery);
 const queryClient=useQueryClient();
 const [tab,setTab]=useState<Tab>("live");
 const [searching,setSearching]=useState(false);
 const [query,setQuery]=useState("");
 const [savingId,setSavingId]=useState<string|null>(null);
 const toggleSaved=async(id:string)=>{
   if(savingId===id)return;
   setSavingId(id);
   try{
     const result=await toggleSavedOpportunity({data:{offerId:id}});
     if(!result.ok){toast.error(result.error??"Could not update saved jobs");return;}
     await queryClient.invalidateQueries({queryKey:["partner-saved-opportunities"]});
   }catch(error){toast.error(error instanceof Error?error.message:"Could not update saved jobs");}
   finally{setSavingId(null)}
 };
 const visible=useMemo(()=>offers.filter((offer:any)=>{
   if(tab==="saved"&&!saved.includes(offer.id))return false;
   if(tab==="invites"&&offer.status!=="sent")return false;
   const q=query.trim().toLowerCase();return !q||[offer.service_name,offer.outward_postcode,offer.scope_summary].some(v=>String(v||"").toLowerCase().includes(q));
 }),[offers,tab,saved,query]);
 return <div className="min-h-screen bg-background pb-24"><AppHeader workspace="partner" />
   <PageShell width="standard" className="partner-opp-main">
    <PageHeader eyebrow="Find work" title="Opportunities" description="Matched local jobs, customer invites and saved work." action={<button type="button" className="header-icon-button" aria-label="Search opportunities" onClick={()=>setSearching(v=>!v)}><Search className="h-5 w-5"/></button>} />
    {searching&&<label className="partner-opp-search"><Search/><input autoFocus value={query} onChange={e=>setQuery(e.target.value)} placeholder="Search jobs or areas"/></label>}
    <div className="partner-opp-tabs" role="tablist">
      <button className={tab==="live"?"active":""} onClick={()=>setTab("live")}>Live Jobs</button>
      <button className={tab==="invites"?"active":""} onClick={()=>setTab("invites")}>Invites</button>
      <button className={tab==="saved"?"active":""} onClick={()=>setTab("saved")}>Saved</button>
    </div>
    <section className="partner-opp-list">
      {visible.length?visible.map((offer:any)=><OpportunityCard key={offer.id} offer={offer} saved={saved.includes(offer.id)} saving={savingId===offer.id} toggleSaved={()=>void toggleSaved(offer.id)}/>):<EmptyState title={tab==="saved"?"No saved jobs yet":"No opportunities right now"} description={tab==="saved"?"Tap the heart on a job to save it here.":"New matched work will appear here."}/>}
    </section>
   </PageShell>
 </div>
}

function OpportunityCard({offer,saved,saving,toggleSaved}:{offer:any;saved:boolean;saving:boolean;toggleSaved:()=>void}){
 const urgent=String(offer.scope_summary||"").toLowerCase().includes("urgent");
 const low=offer.estimate_low_pence!=null?gbp(offer.estimate_low_pence):null;
 const high=offer.estimate_high_pence!=null?gbp(offer.estimate_high_pence):null;
 const price=low&&high?low+" – "+high:low||"Quote required";
 const media=(offer.media||[]).slice(0,4);
 return <article className="partner-opp-card">
   <div className="partner-opp-card-top">
    <Link to="/partner/offers/$offerId" params={{offerId:offer.id}} className="partner-opp-copy">
      <h2>{offer.service_name||"Local job"}</h2>
      <p>{offer.outward_postcode||"Local area"}</p>
      <strong className="partner-opp-price">{price}</strong>
    </Link>
    <button type="button" aria-label={saved?"Remove saved job":"Save job"} aria-pressed={saved} disabled={saving} onClick={toggleSaved} className={"partner-opp-heart "+(saved?"saved":"")}><Heart/></button>
    <Link aria-label="View job" to="/partner/offers/$offerId" params={{offerId:offer.id}} className="partner-opp-chevron"><ArrowRight/></Link>
   </div>
   {media.length>0&&<div className="partner-opp-media">{media.map((item:any,i:number)=><Link key={item.url} to="/partner/offers/$offerId" params={{offerId:offer.id}} className="partner-opp-thumb"><img src={item.url} alt={"Customer job photo "+(i+1)}/></Link>)}</div>}
   {media.length===0&&<div className="partner-opp-no-media"><ImageIcon/><span>Open job to see full details</span></div>}
   <div className="partner-opp-footer">
     <div className="partner-opp-tags"><span className={urgent?"urgent":"flexible"}>{urgent?"Urgent":offer.preferred_window||"Flexible"}</span><span className="trade">{offer.service_name||"Trade"}</span></div>
     <Link to="/partner/offers/$offerId" params={{offerId:offer.id}} search={{action:"quote"} as any} className="partner-opp-quote">Send quote</Link>
   </div>
 </article>
}
